import { all, put, call, takeLatest, takeEvery } from 'redux-saga/effects'
import axios from 'portal/utils/axios-msal'
import _ from 'lodash'
import {
  ALERT_RULE_TITLE_MAPPER,
  determineAlertRuleCount
} from 'portal/components/Settings/AlertRules/constants'
import {
  alertRulesLoadSuccess,
  alertRulesLoadFailure,
  editAlertRulesFailure,
  ALERT_RULES_LOAD,
  COPY_ALERT_RULES,
  COPY_ALERT_RULES_TO_PROJECT_IDS,
  EDIT_ALERT_RULES,
  GET_ALERT_USAGE_BY_PROJECT,
  AlertRulesLoadAction,
  CopyAlertRulesToProjectIdsAction,
  CopyAndSaveAlertRulesAction,
  EditAlertRulesAction,
  GetAlertUsageByProjectAction,
  getAlertUsageByProjectSuccess,
  getAlertUsageByProjectFailure
} from './actions'

type GenericObject = { [key: string]: any }

function* alertRulesLoadSaga(action: AlertRulesLoadAction) {
  const { accountId, projectId } = action

  try {
    let alertRules: GenericObject = {}
    const response: { data: GenericObject } = yield call(() =>
      axios.get(`/accounts/${accountId}/projects/${projectId}/alertrules`, {})
    )

    alertRules = response && response.data ? { ...response.data } : {}

    // update all 'start' and 'end' times to be in the format 'HH:mm'
    Object.keys(alertRules).forEach(alertKey => {
      const alertRule = alertRules[alertKey]
      if (alertRule?.schedule) {
        Object.keys(alertRule.schedule).forEach(day => {
          const schedule = alertRule.schedule[day]
          if (schedule.start) {
            schedule.start = schedule.start.split(':').slice(0, 2).join(':')
          }
          if (schedule.end) {
            schedule.end = schedule.end.split(':').slice(0, 2).join(':')
          }
        })
      }
    })

    yield put(alertRulesLoadSuccess(accountId, projectId, alertRules))
  } catch (error) {
    console.error(error)
    yield put(alertRulesLoadFailure(accountId, projectId, error))
  }
}

function* editAlertRulesSaga(action: EditAlertRulesAction) {
  const {
    accountId,
    projectId,
    alertRules,
    reload,
    showSuccessNotification,
    showFailureNotification
  } = action

  // Take a deep copy of the alert rules to avoid mutating the original state
  const alertRulesCopy = _.cloneDeep(alertRules)

  // Update all 'start' and 'end' times to be in the format 'HH:mm:ss'
  Object.keys(alertRulesCopy).forEach(alertKey => {
    const alertRule = alertRulesCopy[alertKey]
    if (alertRule?.schedule) {
      Object.keys(alertRule.schedule).forEach(day => {
        const schedule = alertRule.schedule[day]
        if (schedule.start) {
          schedule.start = `${schedule.start}:00`
        }
        if (schedule.end) {
          schedule.end = `${schedule.end}:00`
        }
      })
    }
  })

  try {
    yield call(() =>
      axios.put(
        `/accounts/${accountId}/projects/${projectId}/alertrules`,
        alertRulesCopy,
        {
          validateStatus: status => {
            if (status === 200 || status === 201 || status === 204) {
              reload()
              showSuccessNotification()
              return true
            }
            showFailureNotification()
            return false
          }
        }
      )
    )
  } catch (error) {
    yield put(editAlertRulesFailure(accountId, projectId, error))
  }
}

function* copyAndSaveAlertRulesSaga(action: CopyAndSaveAlertRulesAction) {
  const {
    accountId,
    projectId,
    copyFromProjectId,
    reload,
    showSuccessNotification,
    showFailureNotification
  } = action

  let alertRules: GenericObject = {}
  const response: { data: GenericObject } = yield call(() =>
    axios.get(
      `/accounts/${accountId}/projects/${copyFromProjectId}/alertrules`,
      {}
    )
  )

  alertRules = response && response.data ? { ...response.data } : {}

  try {
    yield call(() =>
      axios.put(
        `/accounts/${accountId}/projects/${projectId}/alertrules`,
        alertRules,
        {
          validateStatus: status => {
            if (status === 200 || status === 201 || status === 204) {
              reload()
              showSuccessNotification()
              return true
            }
            showFailureNotification()
            return false
          }
        }
      )
    )
  } catch (error) {
    yield put(editAlertRulesFailure(accountId, projectId, error))
  }
}

function* copyAlertRulesToProjectIdsSaga(
  action: CopyAlertRulesToProjectIdsAction
) {
  const {
    accountId,
    projectId,
    copyToProjectIds,
    allProjectIds,
    reload,
    showSuccessNotification,
    showFailureNotification
  } = action

  let alertRules: GenericObject = {}
  const response: { data: GenericObject } = yield call(() =>
    axios.get(`/accounts/${accountId}/projects/${projectId}/alertrules`, {})
  )

  alertRules = response && response.data ? { ...response.data } : {}

  try {
    let failureNotificationShown = false

    const copyAlertRulesToProject = (copyToProjectId: string) => {
      return axios.put(
        `/accounts/${accountId}/projects/${copyToProjectId}/alertrules`,
        alertRules,
        {
          validateStatus: status => {
            if (status === 200 || status === 201 || status === 204) {
              return true
            }
            if (!failureNotificationShown) {
              showFailureNotification()
              failureNotificationShown = true
            }
            return false
          }
        }
      )
    }

    for (const copyToProjectId of copyToProjectIds) {
      yield call(copyAlertRulesToProject, copyToProjectId)
    }

    // Refresh Active alerts data for the affected projects
    yield call(getAlertUsageByProjectSaga, {
      type: GET_ALERT_USAGE_BY_PROJECT,
      accountId,
      projectIds: allProjectIds
    })
    if (!failureNotificationShown) {
      reload()
      showSuccessNotification()
    }
  } catch (error) {
    yield put(editAlertRulesFailure(accountId, projectId, error))
  }
}

export function* getAlertUsageByProjectSaga(
  action: GetAlertUsageByProjectAction
) {
  const { accountId, projectIds } = action

  const alertUsage: GenericObject = {}

  try {
    // For each project ID, get the alert rules and count them
    for (const copyFromProjectId of projectIds) {
      const response: { data: GenericObject } = yield call(() =>
        axios.get(
          `/accounts/${accountId}/projects/${copyFromProjectId}/alertrules`,
          {}
        )
      )

      alertUsage[copyFromProjectId] =
        response && response.data
          ? {
              data: response.data,
              count: Object.entries(response.data)
                .filter(
                  ([key, rule]) =>
                    rule != null && ALERT_RULE_TITLE_MAPPER[key] !== undefined
                ) // duplicate logic from removeEmptyOrUnmapped
                .reduce(
                  (count: number, [key, rule]: any) =>
                    count + determineAlertRuleCount(key, rule),
                  0
                )
            }
          : { data: {}, count: 0 }
    }

    yield put(getAlertUsageByProjectSuccess(accountId, projectIds, alertUsage))
  } catch (error) {
    console.error(error)
    yield put(getAlertUsageByProjectFailure(accountId, projectIds, error))
  }
}

export function* watch() {
  yield all([takeLatest(ALERT_RULES_LOAD, alertRulesLoadSaga)])
  yield takeEvery(EDIT_ALERT_RULES, editAlertRulesSaga)
  yield takeEvery(
    COPY_ALERT_RULES_TO_PROJECT_IDS,
    copyAlertRulesToProjectIdsSaga
  )
  yield takeEvery(COPY_ALERT_RULES, copyAndSaveAlertRulesSaga)
  yield takeEvery(GET_ALERT_USAGE_BY_PROJECT, getAlertUsageByProjectSaga)
}
